import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Flame } from "lucide-react";
import { useTimeFormatter } from "@/lib/timeFormat";

const goalLabels = {
  bulking: "Bulking",
  cutting: "Cutting",
  maintenance: "Maintenance",
  clean_eating: "Clean Eating",
};

const goalColors = {
  bulking: "bg-primary/10 text-primary",
  cutting: "bg-destructive/10 text-destructive",
  maintenance: "bg-emerald-500/10 text-emerald-500",
  clean_eating: "bg-amber-500/10 text-amber-500",
};

function to12Hour(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

export default function MealPlanDetailsDialog({
  open,
  onOpenChange,
  plan,
  isLoading,
  onAssign,
  onCancel,
}) {
  const fmtTime = useTimeFormatter();
  const [isAssigning, setIsAssigning] = useState(false);

  const handleAssign = async () => {
    setIsAssigning(true);
    await onAssign();
    setIsAssigning(false);
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100%-2rem)] md:w-full rounded-xl md:rounded-lg">
        <DialogHeader>
          <DialogTitle>{plan.name}</DialogTitle>
        </DialogHeader>
        <HealthSourceNote topic="planning" />

        <ScrollArea className="h-96">
          <div className="space-y-4 pr-4">
            {/* Overview */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Overview</h3>
              <div className="flex flex-wrap gap-2">
                {plan.goal && (
                  <Badge
                    variant="secondary"
                    className={`${goalColors[plan.goal]}`}
                  >
                    {goalLabels[plan.goal]}
                  </Badge>
                )}
                {plan.description && (
                  <p className="text-xs text-muted-foreground">
                    {plan.description}
                  </p>
                )}
              </div>
            </div>

            {/* Macros */}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Daily Macros</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {plan.daily_calories && (
                  <div className="p-2 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Flame className="w-3 h-3" />
                      Calories
                    </div>
                    <p className="font-semibold text-sm">{plan.daily_calories}</p>
                  </div>
                )}
                {plan.daily_protein_g && (
                  <div className="p-2 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground">Protein</div>
                    <p className="font-semibold text-sm">
                      {plan.daily_protein_g}g
                    </p>
                  </div>
                )}
                {plan.daily_carbs_g && (
                  <div className="p-2 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground">Carbs</div>
                    <p className="font-semibold text-sm">
                      {plan.daily_carbs_g}g
                    </p>
                  </div>
                )}
                {plan.daily_fat_g && (
                  <div className="p-2 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground">Fat</div>
                    <p className="font-semibold text-sm">{plan.daily_fat_g}g</p>
                  </div>
                )}
              </div>
            </div>

            {/* Meals */}
            {plan.meals && plan.meals.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Meals</h3>
                <div className="space-y-3">
                  {plan.meals.map((meal, idx) => (
                    <div
                      key={idx}
                      className="p-2 rounded-lg border bg-card/50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm">{meal.meal_name}</p>
                        {meal.time && (
                          <span className="text-xs text-muted-foreground">
                            {fmtTime(meal.time)}
                          </span>
                        )}
                      </div>
                      {meal.foods && meal.foods.length > 0 && (
                        <div className="space-y-1">
                          {meal.foods.map((food, fi) => (
                            <div key={fi} className="text-xs">
                              <p className="text-foreground">
                                {food.name} - {food.portion}
                              </p>
                              <div className="text-muted-foreground flex gap-2 text-[10px]">
                                {food.calories && (
                                  <span>{food.calories} kcal</span>
                                )}
                                {food.protein_g && (
                                  <span>P: {food.protein_g}g</span>
                                )}
                                {food.carbs_g && (
                                  <span>C: {food.carbs_g}g</span>
                                )}
                                {food.fat_g && (
                                  <span>F: {food.fat_g}g</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {onAssign && (
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={isAssigning}>
              {isAssigning ? "Assigning..." : "Assign Plan"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}